import { useEffect, useState } from 'react';
import { BrowserRouter, Link, Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { api } from './lib/api';
import { loadProfile, ratingRank, saveProfile, type PlayerProfile } from './lib/profile';
import { AcademyLessonPage, AcademyPage } from './components/Academy';
import { AiPage, AnalysisPage, Dashboard, FeedbackPage, MatchPage, MovementTrainingPage, NotFoundPage, OnlinePage, ProfilePage, StudiesPage, TacticsPage, TournamentsPage } from './pages';

const primaryNav = [
  { to: '/play/online', label: '대국', sub: '온라인 대국', icon: 'play' },
  { to: '/tactics', label: '전술', sub: '실전 전술', icon: 'puzzle' },
  { to: '/learn', label: '배우기', sub: '강의와 스터디', icon: 'learn' },
  { to: '/tournaments', label: '대회', sub: '아레나와 스위스', icon: 'trophy' },
  { to: '/analysis', label: '분석', sub: 'Fairy-Stockfish', icon: 'analysis' },
];

function BrandMark() {
  return <svg className="brand-mark" viewBox="0 0 48 48" aria-hidden="true">
    <path className="brand-mark__tile" d="M14 3h20l11 11v20L34 45H14L3 34V14Z" />
    <path className="brand-mark__palace" d="M14.5 14.5h19v19h-19Zm0 0 19 19m0-19-19 19" />
    <circle className="brand-mark__centre" cx="24" cy="24" r="2.8" />
  </svg>;
}

function NavGlyph({ name }: { name: string }) {
  const paths: Record<string, React.ReactNode> = {
    play: <><path d="M7 16.5 12.5 11 18 16.5"/><path d="M6.5 7.5h11v10h-11z"/></>,
    puzzle: <><path d="M9.2 5.5a2.8 2.8 0 1 1 5.6 0V7H17v3.2h1.5a2.8 2.8 0 1 1 0 5.6H17V19H7v-3.2H5.5a2.8 2.8 0 1 1 0-5.6H7V7h2.2z"/></>,
    learn: <><path d="M4.5 6.5c3.2-.8 5.7-.2 7.5 1.4v11c-1.8-1.6-4.3-2.2-7.5-1.4z"/><path d="M19.5 6.5c-3.2-.8-5.7-.2-7.5 1.4v11c1.8-1.6 4.3-2.2 7.5-1.4z"/></>,
    trophy: <><path d="M8 4.5h8v4.2c0 3-1.6 5.2-4 5.2s-4-2.2-4-5.2z"/><path d="M8 7H5.2v1.3c0 2.1 1.1 3.3 3.2 3.5M16 7h2.8v1.3c0 2.1-1.1 3.3-3.2 3.5M12 14v3.5M8.5 19.5h7"/></>,
    analysis: <><path d="M5 18.5V13m4.7 5.5V9m4.6 9.5V5.5m4.7 13V11"/></>,
  };
  return <svg viewBox="0 0 24 24" aria-hidden="true">{paths[name]}</svg>;
}

function App() {
  const [profile, setProfileState] = useState<PlayerProfile>(loadProfile);
  const setProfile = (next: PlayerProfile) => { setProfileState(next); saveProfile(next); };

  useEffect(() => {
    if (!api.configured) return;
    api.getProfile(profile.id).then(setProfile).catch(() => api.saveProfile(profile).catch(() => undefined));
  }, []);

  return (
    <BrowserRouter>
      <div className="product-shell">
        <aside className="site-rail">
          <Link className="app-brand" to="/"><BrandMark/><div><strong>장기마루</strong><small>JANGGI MARU</small></div></Link>
          <nav className="desktop-nav" aria-label="장기마루 메뉴">
            {primaryNav.map((item) => <NavLink to={item.to} key={item.to} title={item.sub} aria-label={`${item.label} · ${item.sub}`}><NavGlyph name={item.icon}/><span><b>{item.label}</b><small>{item.sub}</small></span></NavLink>)}
          </nav>
          <div className="rail-tools">
            <NavLink className="tool-link" to="/feedback" aria-label="익명 의견 남기기"><span>?</span><div><b>의견 남기기</b><small>익명 베타 피드백</small></div></NavLink>
            <NavLink className="top-profile" to="/me"><span>{profile.handle.slice(0,1)}</span><div><b>{profile.handle}</b><small>{profile.rating} · {ratingRank(profile.rating)}</small></div></NavLink>
          </div>
        </aside>

        <header className="mobile-header">
          <Link className="app-brand" to="/"><BrandMark/><div><strong>장기마루</strong><small>JANGGI MARU</small></div></Link>
          <NavLink className="mobile-profile" to="/me">{profile.rating}</NavLink>
        </header>

        <main className="app-main">
          <Routes>
            <Route path="/" element={<Dashboard profile={profile} setProfile={setProfile}/>} />
            <Route path="/tactics" element={<TacticsPage/>} />
            <Route path="/bakbo" element={<Navigate to="/tactics" replace/>} />
            <Route path="/puzzles" element={<Navigate to="/tactics" replace/>} />
            <Route path="/play/online" element={<OnlinePage profile={profile} setProfile={setProfile}/>} />
            <Route path="/play/online/:matchId" element={<MatchPage profile={profile} setProfile={setProfile}/>} />
            <Route path="/play/ai" element={<AiPage/>} />
            <Route path="/analysis" element={<AnalysisPage/>} />
            <Route path="/learn" element={<AcademyPage/>} />
            <Route path="/learn/:courseId/:lessonId" element={<AcademyLessonPage/>} />
            <Route path="/learn/moves" element={<MovementTrainingPage/>} />
            <Route path="/studies" element={<StudiesPage profile={profile} setProfile={setProfile}/>} />
            <Route path="/tournaments" element={<TournamentsPage profile={profile} setProfile={setProfile}/>} />
            <Route path="/feedback" element={<FeedbackPage/>} />
            <Route path="/me" element={<ProfilePage profile={profile} setProfile={setProfile}/>} />
            <Route path="*" element={<NotFoundPage/>} />
          </Routes>
        </main>

        <nav className="bottom-nav" aria-label="모바일 메뉴">
          <NavLink end to="/"><span className="bottom-home">⌂</span><b>홈</b></NavLink>
          {primaryNav.map((item) => <NavLink to={item.to} key={item.to}><NavGlyph name={item.icon}/><b>{item.label}</b></NavLink>)}
        </nav>
      </div>
    </BrowserRouter>
  );
}

export default App;
